import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Briefcase, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import apiClient from '../config/axios';
import './Careers.css';

const Careers = () => {
  const [careers, setCareers] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const fetchCareers = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/careers');
      setCareers(response.data.data || getDefaultCareers());
    } catch (error) {
      setCareers(getDefaultCareers());
    }
  }, []);

  useEffect(() => {
    fetchCareers();
  }, [fetchCareers]);

  const getDefaultCareers = () => [
    {
      id: 1,
      title: 'Embedded Systems Engineer',
      department: 'Engineering',
      location: 'Hyderabad',
      type: 'Full-time',
      description: 'We are looking for an experienced Embedded Systems Engineer to join our team. You will be responsible for designing and developing firmware for various embedded systems projects.',
      requirements: 'B.Tech/M.Tech in Electronics/Computer Science\n3+ years of experience in embedded systems\nProficiency in C/C++ programming\nExperience with ARM microcontrollers\nKnowledge of communication protocols (SPI, I2C, UART)\nExperience with RTOS is a plus',
      is_active: true
    },
    {
      id: 2,
      title: 'PCB Design Engineer',
      department: 'Hardware',
      location: 'Hyderabad',
      type: 'Full-time',
      description: 'Join our hardware team as a PCB Design Engineer. You will work on designing high-quality PCB layouts for various industrial and consumer products.',
      requirements: 'B.Tech in Electronics/Electrical Engineering\n2+ years of PCB design experience\nProficiency in Altium Designer or KiCad\nKnowledge of high-speed digital design\nExperience with multi-layer PCB design\nFamiliarity with DFM/DFA guidelines',
      is_active: true
    },
    {
      id: 3,
      title: 'IoT Developer',
      department: 'Software',
      location: 'Hyderabad / Remote',
      type: 'Full-time',
      description: 'We are seeking an IoT Developer to work on cutting-edge IoT solutions. You will develop cloud-connected applications and device firmware for our IoT products.',
      requirements: 'B.Tech in Computer Science or related field\n2+ years of IoT development experience\nExperience with AWS IoT or Azure IoT\nProficiency in Python, Node.js, or similar\nKnowledge of MQTT, CoAP protocols\nExperience with wireless protocols (WiFi, BLE, LoRa)',
      is_active: true
    },
    {
      id: 4,
      title: 'Hardware Test Engineer',
      department: 'Quality',
      location: 'Hyderabad',
      type: 'Full-time',
      description: 'Join our quality team as a Hardware Test Engineer. You will be responsible for testing and validating electronic hardware designs.',
      requirements: 'B.Tech in Electronics Engineering\n1+ years of hardware testing experience\nFamiliarity with test equipment (oscilloscopes, multimeters)\nKnowledge of automated testing\nExperience with EMC/EMI testing is a plus\nGood documentation skills',
      is_active: true
    }
  ];

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const benefits = [
    { title: 'Competitive Salary', description: 'Industry-leading compensation packages' },
    { title: 'Health Insurance', description: 'Comprehensive medical coverage for you and family' },
    { title: 'Learning & Growth', description: 'Continuous learning opportunities and certifications' },
    { title: 'Work-Life Balance', description: 'Flexible working hours and remote options' },
    { title: 'Cutting-Edge Tech', description: 'Work with the latest technologies and tools' },
    { title: 'Team Events', description: 'Regular team activities and celebrations' }
  ];

  return (
    <div className="careers-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Careers</h1>
          <p>Join our team and build the future of embedded systems</p>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="why-join section">
        <div className="container">
          <div className="section-title">
            <h2>Why Join Us?</h2>
            <p>Be part of a team that's shaping the future of embedded technology</p>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card card">
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="positions-section section bg-light">
        <div className="container">
          <div className="section-title">
            <h2>Open Positions</h2>
            <p>Explore opportunities to grow your career with us</p>
          </div>

          {careers.length > 0 ? (
            <div className="positions-list">
              {careers.map((career) => (
                <div key={career.id} className="position-card">
                  <div className="position-header" onClick={() => toggleExpand(career.id)}>
                    <div className="position-info">
                      <h3>{career.title}</h3>
                      <div className="position-meta">
                        <span><Briefcase size={16} /> {career.department}</span>
                        <span><MapPin size={16} /> {career.location}</span>
                        <span><Clock size={16} /> {career.type}</span>
                      </div>
                    </div>
                    <button className="expand-btn">
                      {expandedId === career.id ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </button>
                  </div>

                  {expandedId === career.id && (
                    <div className="position-details">
                      <div className="details-content">
                        <div className="detail-section">
                          <h4>Job Description</h4>
                          <p>{career.description}</p>
                        </div>
                        <div className="detail-section">
                          <h4>Requirements</h4>
                          <ul>
                            {career.requirements.split('\n').map((req, idx) => (
                              <li key={idx}>{req}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="apply-section">
                        <Link to="/contact" className="btn btn-primary">
                          Apply Now <ArrowRight size={18} />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-positions">
              <p>No open positions at the moment. Please check back later.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="careers-cta section">
        <div className="container">
          <div className="cta-content">
            <h2>Don't See a Perfect Fit?</h2>
            <p>
              We're always looking for talented individuals. Send us your resume
              and we'll keep you in mind for future opportunities.
            </p>
            <Link to="/contact" className="btn btn-primary">
              Send Your Resume <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
